"use client";

import { useState } from "react";
import { ScrollProgress } from "@/components/layout/ScrollProgress";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useI18n } from '@/lib/i18n/context';

interface Props { params: { tag: string } }

const appName = process.env.NEXT_PUBLIC_APP_NAME || "Hummini";

type FormState = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useI18n();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, subject, message }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErrorMessage(data.error || t.contact.errorMessage);
        setStatus("error");
        return;
      }

      setStatus("success");
    } catch {
      setErrorMessage(t.contact.errorMessage);
      setStatus("error");
    }
  }

  const inputClass =
    "w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors";

  return (
    <>
      <ScrollProgress />
      <Header />
      <main className="min-h-screen bg-[#0a0a0a] text-zinc-400 px-4 py-16 md:py-24">
        <div className="max-w-2xl mx-auto">

          <header className="border-b border-zinc-800 pb-10 mb-10">
            <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-zinc-600 mb-5">
              {appName}
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-normal text-white leading-tight tracking-tight mb-4">
              {t.contact.title}
            </h1>
            <p className="text-sm leading-relaxed text-zinc-400">
              {t.contact.subtitle}
            </p>
          </header>

          {status === "success" ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-6 py-10 text-center">
              <p className="font-display text-2xl text-white mb-3">{t.contact.successTitle}</p>
              <p className="text-sm text-zinc-400">
                {t.contact.successMessage}
              </p>
              <button
                onClick={() => {
                  setName(""); setEmail(""); setSubject(""); setMessage("");
                  setStatus("idle");
                }}
                className="mt-6 text-xs font-mono tracking-widest uppercase text-zinc-500 hover:text-white transition-colors"
              >
                {t.contact.submitButton}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600">
                    {t.contact.name} <span className="text-zinc-700">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600">
                    {t.contact.email} <span className="text-zinc-700">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600">
                  {t.contact.subject} <span className="text-zinc-700">*</span>
                </label>
                <select
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className={`${inputClass} cursor-pointer`}
                >
                  <option value="" disabled>{t.contact.value_default}</option>
                  <option value="general">{t.contact.value_general}</option>
                  <option value="report">{t.contact.value_report}</option>
                  <option value="account">{t.contact.value_account}</option>
                  <option value="business">{t.contact.value_business}</option>
                  <option value="feedback">{t.contact.value_feedback}</option>
                  <option value="other">{t.contact.value_other}</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600">
                  {t.contact.message} <span className="text-zinc-700">*</span>
                </label>
                <textarea
                  required
                  rows={6}
                  placeholder="Tell us what's on your mind..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {status === "error" && (
                <p className="text-sm text-red-400">
                  {errorMessage || "Something went wrong. Please try again."}
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full sm:w-auto px-8 py-3 bg-white text-black text-sm font-medium rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === "loading" ? "Sending…" : "Send Message"}
              </button>

            </form>
          )}

          {/* Extra info */}
          <div className="mt-16 pt-10 border-t border-zinc-800 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600 mb-2">
                {t.contact.res_time_title}
              </p>
              <p className="text-sm text-zinc-400">
                {t.contact.res_time}
              </p>
            </div>
            <div>
              <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-600 mb-2">
                {t.contact.report_content}
              </p>
              <p className="text-sm text-zinc-400">
                {t.contact.report_content_hint}
              </p>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </>
  );
}